// Real-time Updates Utility for Admin Panel Integration
const ADMIN_API_BASE = 'https://ton.wallet.tflash.xyz/admin/api';

class RealtimeUpdates {
    constructor() {
        this.lastCheck = Math.floor(Date.now() / 1000);
        this.updateCallbacks = new Map();
        this.settingsCallbacks = new Map();
        this.tokenCallbacks = new Map();
        this.isPolling = false;
        this.pollInterval = 10000; // 10 seconds
    }

    // Start polling for updates
    startPolling() {
        if (this.isPolling) return;
        
        this.isPolling = true;
        this.pollForUpdates();
    }

    // Stop polling
    stopPolling() {
        this.isPolling = false;
    }

    // Poll for updates from admin panel
    async pollForUpdates() {
        if (!this.isPolling) return;

        try {
            const response = await fetch(`${ADMIN_API_BASE}/updates.php?action=check_updates&last_check=${this.lastCheck}`);
            const data = await response.json();

            if (data.success && data.updates.length > 0) {
                console.log('🔄 Real-time updates received:', data.updates);
                
                // Process each update
                for (const update of data.updates) {
                    await this.processUpdate(update);
                }
                
                this.lastCheck = data.current_time;
            }
        } catch (error) {
            console.warn('Failed to check for updates:', error);
        }

        // Schedule next poll
        setTimeout(() => this.pollForUpdates(), this.pollInterval);
    }

    // Process individual update
    async processUpdate(update) {
        try {
            const updateData = JSON.parse(update.update_data);
            
            switch (update.update_type) {
                case 'fee_update':
                    this.notifyFeeUpdate(updateData);
                    break;
                    
                case 'token_update':
                    this.notifyTokenUpdate(updateData);
                    break;
                    
                case 'settings_update':
                    this.notifySettingsUpdate(updateData);
                    break;
                    
                case 'maintenance_update':
                    this.notifyMaintenanceUpdate(updateData);
                    break;
                    
                case 'fee_wallet_update':
                    this.notifyFeeWalletUpdate(updateData);
                    break;
                    
                default:
                    console.log('Unknown update type:', update.update_type);
            }
        } catch (error) {
            console.error('Error processing update:', error);
        }
    }

    // Notify fee updates
    notifyFeeUpdate(data) {
        console.log('💰 Fee update received:', data);
        
        // Update local storage
        localStorage.setItem('admin_ton_fee', data.ton_fee);
        localStorage.setItem('admin_bnb_fee', data.bnb_fee);
        
        // Notify callbacks
        this.updateCallbacks.forEach(callback => {
            if (typeof callback === 'function') {
                callback('fee_update', data);
            }
        });
        
        // Toast notification is handled in WalletContext, no need to duplicate here
    }

    // Notify token updates
    notifyTokenUpdate(data) {
        console.log('🪙 Token update received:', data);
        
        // Update local storage
        const tokens = JSON.parse(localStorage.getItem('admin_tokens') || '[]');
        const existingIndex = tokens.findIndex(t => t.id === data.token_id);
        
        if (existingIndex >= 0) {
            tokens[existingIndex] = data;
        } else {
            tokens.push(data);
        }
        
        localStorage.setItem('admin_tokens', JSON.stringify(tokens));
        
        // Notify callbacks
        this.tokenCallbacks.forEach(callback => {
            if (typeof callback === 'function') {
                callback('token_update', data);
            }
        });
        
        // Toast notification is handled in WalletContext, no need to duplicate here
    }

    // Notify settings updates
    notifySettingsUpdate(data) {
        console.log('⚙️ Settings update received:', data);
        
        // Update local storage
        localStorage.setItem('admin_settings', JSON.stringify(data));
        
        // Notify callbacks
        this.settingsCallbacks.forEach(callback => {
            if (typeof callback === 'function') {
                callback('settings_update', data);
            }
        });
        
        // Toast notification is handled in WalletContext, no need to duplicate here
    }

    // Notify maintenance updates
    notifyMaintenanceUpdate(data) {
        console.log('🔧 Maintenance update received:', data);
        
        // Get current maintenance mode from localStorage to check if it changed
        const currentMaintenanceMode = localStorage.getItem('maintenance_mode');
        const newMaintenanceMode = data.maintenance_mode;
        
        // Update local storage with all maintenance data
        localStorage.setItem('maintenance_mode', data.maintenance_mode);
        localStorage.setItem('maintenance_message', data.maintenance_message);
        localStorage.setItem('maintenance_eta', data.maintenance_eta);
        localStorage.setItem('maintenance_progress', data.maintenance_progress);
        localStorage.setItem('maintenance_type', data.maintenance_type);
        localStorage.setItem('maintenance_email', data.maintenance_email);
        localStorage.setItem('maintenance_twitter', data.maintenance_twitter);
        
        // Notify callbacks
        this.updateCallbacks.forEach(callback => {
            if (typeof callback === 'function') {
                callback('maintenance_update', data);
            }
        });
        
        // Only show progress update notification (not status change - that's handled in WalletContext)
        if (newMaintenanceMode === '1' && data.maintenance_progress) {
            const progress = data.maintenance_progress || 0;
            this.showNotification('Maintenance progress', `Progress: ${progress}%`);
        }
    }

    // Notify fee wallet updates
    notifyFeeWalletUpdate(data) {
        console.log('💳 Fee wallet update received:', data);
        
        // Update local storage
        localStorage.setItem('fee_wallets', JSON.stringify({
            TON: {
                mainnet: data.ton_mainnet_wallet,
                testnet: data.ton_testnet_wallet
            },
            BNB: {
                mainnet: data.bnb_mainnet_wallet,
                testnet: data.bnb_testnet_wallet
            }
        }));
        
        // Notify callbacks
        this.updateCallbacks.forEach(callback => {
            if (typeof callback === 'function') {
                callback('fee_wallet_update', data);
            }
        });
        
        // Toast notification is handled in WalletContext, no need to duplicate here
    }

    // Show notification to user
    showNotification(title, message) {
        // Check if browser supports notifications
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(title, { body: message });
        }
        
        // Also show toast notification
        if (window.showToast) {
            window.showToast('info', `${title}: ${message}`);
        }
    }

    // Register callback for updates
    onUpdate(callback) {
        const id = Date.now() + Math.random();
        this.updateCallbacks.set(id, callback);
        return id;
    }

    // Register callback for settings updates
    onSettingsUpdate(callback) {
        const id = Date.now() + Math.random();
        this.settingsCallbacks.set(id, callback);
        return id;
    }

    // Register callback for token updates
    onTokenUpdate(callback) {
        const id = Date.now() + Math.random();
        this.tokenCallbacks.set(id, callback);
        return id;
    }

    // Remove callback
    removeCallback(id) {
        this.updateCallbacks.delete(id);
        this.settingsCallbacks.delete(id);
        this.tokenCallbacks.delete(id);
    }

    // Get current settings from admin panel
    async getSettings() {
        try {
            const response = await fetch(`${ADMIN_API_BASE}/updates.php?action=get_settings`);
            const data = await response.json();
            
            if (data.success) {
                // Cache settings
                localStorage.setItem('admin_settings', JSON.stringify(data.settings));
                return data.settings;
            }
        } catch (error) {
            console.warn('Failed to get settings:', error);
        }
        
        // Return cached settings if available
        const cached = localStorage.getItem('admin_settings');
        return cached ? JSON.parse(cached) : {};
    }

    // Get current tokens from admin panel
    async getTokens() {
        try {
            const response = await fetch(`${ADMIN_API_BASE}/updates.php?action=get_tokens`);
            const data = await response.json();
            
            if (data.success) {
                // Cache tokens
                localStorage.setItem('admin_tokens', JSON.stringify(data.tokens));
                return data.tokens;
            }
        } catch (error) {
            console.warn('Failed to get tokens:', error);
        }
        
        // Return cached tokens if available
        const cached = localStorage.getItem('admin_tokens');
        return cached ? JSON.parse(cached) : [];
    }

    // Request notification permission
    async requestNotificationPermission() {
        if ('Notification' in window && Notification.permission === 'default') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return Notification.permission === 'granted';
    }
}

// Create singleton instance
const realtimeUpdates = new RealtimeUpdates();

export default realtimeUpdates; 